import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Orders } from '../api/broker';
import { useToast } from '../components/Toast';
import { formatMoney } from '../components/CertCells';
import { ImageWithHoverPreview } from '../../components/CardHoverPreview/CardHoverPreview';
import { formatEastern } from '../../utils/formatDate';
import './OrdersTab.css';

const STATUS = {
    NEW: { key: 'new', label: 'Awaiting Confirmation' },
    CONFIRMED: { key: 'confirmed', label: 'Awaiting Fulfillment' },
    FULFILLED: { key: 'fulfilled', label: 'Fulfilled' },
};

const statusFor = (order) => {
    if (!order.order_confirmation_id) return STATUS.NEW;
    if (!order.order_fulfillment_id) return STATUS.CONFIRMED;
    return STATUS.FULFILLED;
};

const FILTERS = [
    ['all', 'All'],
    ['new', STATUS.NEW.label],
    ['confirmed', STATUS.CONFIRMED.label],
    ['fulfilled', STATUS.FULFILLED.label],
];

const formatDate = (d) => formatEastern(d, { month: 'short', day: 'numeric', year: 'numeric' });

const psaUrl = (certNumber) => `https://www.psacard.com/cert/${encodeURIComponent(certNumber)}/psa`;

const ExternalIcon = () => (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M14 4h6v6" />
        <line x1="20" y1="4" x2="11" y2="13" />
        <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
    </svg>
);

const Chevron = ({ open }) => (
    <svg className={`bo-chev${open ? ' is-open' : ''}`} viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

// Links inside a row shouldn't also toggle the row open/closed.
const stop = (e) => e.stopPropagation();

// Broker orders: one row per order (image, order #, buyer, price, status).
// Click a row to expand it for the cert number, card, order date, tracking and
// the next action (Confirm, then Fulfill with a tracking number).
const OrdersTab = ({ brokerEmail, filters, goTo, onCountChange }) => {
    const toast = useToast();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [trackingNumbers, setTrackingNumbers] = useState({});
    const [busyRows, setBusyRows] = useState({});
    const [expanded, setExpanded] = useState({});
    const [query, setQuery] = useState(filters.q || '');
    const [statusFilter, setStatusFilter] = useState(filters.status || 'all');

    useEffect(() => {
        setQuery(filters.q || '');
        setStatusFilter(filters.status || 'all');
    }, [filters.q, filters.status]);

    const setRowBusy = (orderNumber, v) => setBusyRows((prev) => ({ ...prev, [orderNumber]: v }));
    const toggle = (orderNumber) => setExpanded((prev) => ({ ...prev, [orderNumber]: !prev[orderNumber] }));

    const load = useCallback(async () => {
        if (!brokerEmail) return;
        setLoading(true);
        setError(null);
        try {
            const result = await Orders.listForSeller(brokerEmail);
            const list = result.orders || [];
            setOrders(list);
            onCountChange?.(list.length);
        } catch (e) {
            setError(e.message);
            setOrders([]);
            onCountChange?.(0);
        } finally {
            setLoading(false);
        }
    }, [brokerEmail, onCountChange]);

    useEffect(() => { load(); }, [load]);

    const handleConfirm = async (order) => {
        setRowBusy(order.order_number, true);
        try {
            await Orders.confirm(order.order_number);
            toast.success(`Confirmed order ${order.order_number}`);
            await load();
        } catch (e) {
            toast.error(`Failed to confirm ${order.order_number}: ${e.message}`);
        } finally {
            setRowBusy(order.order_number, false);
        }
    };

    const handleFulfill = async (order) => {
        const tracking = (trackingNumbers[order.order_number] || '').trim();
        if (!tracking && !window.confirm(`Fulfill order ${order.order_number} without a tracking number?`)) return;
        setRowBusy(order.order_number, true);
        try {
            await Orders.fulfill({ orderNumber: order.order_number, trackingNumber: tracking });
            toast.success(`Fulfilled order ${order.order_number}`);
            await load();
        } catch (e) {
            toast.error(`Failed to fulfill ${order.order_number}: ${e.message}`);
        } finally {
            setRowBusy(order.order_number, false);
        }
    };

    const counts = useMemo(() => {
        const c = { all: orders.length, new: 0, confirmed: 0, fulfilled: 0 };
        for (const o of orders) c[statusFor(o).key] += 1;
        return c;
    }, [orders]);

    const visible = useMemo(() => {
        const q = (query || '').toLowerCase();
        return orders
            .filter((o) => statusFilter === 'all' || statusFor(o).key === statusFilter)
            .filter((o) => !q || [o.order_number, o.buyer_email, o.cert_number, o.cert_name]
                .some((v) => v && String(v).toLowerCase().includes(q)));
    }, [orders, query, statusFilter]);

    const setStatus = (s) => {
        setStatusFilter(s);
        goTo({ tab: 'orders', status: s === 'all' ? undefined : s, q: query || undefined });
    };

    const renderDetails = (order, s, busy) => (
        <div className="bo-details">
            <dl className="bo-facts">
                <div>
                    <dt>Cert</dt>
                    <dd>
                        {order.cert_number ? (
                            <a href={psaUrl(order.cert_number)} target="_blank" rel="noopener noreferrer" className="bo-chip bo-chip-link" title="View on PSA">
                                {order.cert_number}
                                <ExternalIcon />
                            </a>
                        ) : '—'}
                    </dd>
                </div>
                {order.cert_name && (
                    <div>
                        <dt>Card</dt>
                        <dd>{order.cert_name}</dd>
                    </div>
                )}
                {order.date_created && (
                    <div>
                        <dt>Ordered</dt>
                        <dd>{formatDate(order.date_created)}</dd>
                    </div>
                )}
                {order.tracking_number && (
                    <div>
                        <dt>Tracking</dt>
                        <dd><span className="bo-chip bo-mono">{order.tracking_number}</span></dd>
                    </div>
                )}
            </dl>

            {s.key === 'new' && (
                <div className="bo-action">
                    <button type="button" className="bo-btn" disabled={busy} onClick={() => handleConfirm(order)}>
                        {busy ? 'Confirming…' : 'Confirm Order'}
                    </button>
                </div>
            )}
            {s.key === 'confirmed' && (
                <div className="bo-action">
                    <input
                        type="text"
                        className="bo-tracking"
                        placeholder="Tracking number"
                        aria-label={`Tracking number for order ${order.order_number}`}
                        value={trackingNumbers[order.order_number] ?? ''}
                        onChange={(e) => setTrackingNumbers((prev) => ({ ...prev, [order.order_number]: e.target.value }))}
                        onKeyDown={(e) => e.key === 'Enter' && handleFulfill(order)}
                        disabled={busy}
                    />
                    <button type="button" className="bo-btn" disabled={busy} onClick={() => handleFulfill(order)}>
                        {busy ? 'Saving…' : 'Mark Fulfilled'}
                    </button>
                </div>
            )}
        </div>
    );

    return (
        <div className="broker-tab-panel">
            <div className="bo-toolbar">
                <div className="bo-filters" role="tablist" aria-label="Filter by status">
                    {FILTERS.map(([k, label]) => (
                        <button
                            key={k}
                            type="button"
                            role="tab"
                            aria-selected={statusFilter === k}
                            className={`bo-filter${statusFilter === k ? ' is-active' : ''}`}
                            onClick={() => setStatus(k)}
                        >
                            {label}
                            <span className="bo-filter-count">{counts[k]}</span>
                        </button>
                    ))}
                </div>
                <input
                    className="bo-search"
                    placeholder="Search order #, buyer, cert or card"
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        goTo({
                            tab: 'orders',
                            status: statusFilter === 'all' ? undefined : statusFilter,
                            q: e.target.value || undefined,
                        });
                    }}
                />
            </div>

            {error && <p className="broker-error">{error}</p>}
            {loading && <p className="broker-loading">Loading orders…</p>}

            {!loading && !error && (
                visible.length === 0 ? (
                    <p className="broker-empty">No orders match this filter.</p>
                ) : (
                    <ul className="bo-list">
                        {visible.map((order) => {
                            const s = statusFor(order);
                            const busy = !!busyRows[order.order_number];
                            const open = !!expanded[order.order_number];
                            return (
                                <li key={order.order_number} className={`bo-item${open ? ' is-open' : ''}${busy ? ' is-busy' : ''}`}>
                                    <div
                                        className="bo-row"
                                        role="button"
                                        tabIndex={0}
                                        aria-expanded={open}
                                        onClick={() => toggle(order.order_number)}
                                        onKeyDown={(e) => {
                                            if (e.target !== e.currentTarget) return;
                                            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(order.order_number); }
                                        }}
                                    >
                                        <span className="bo-thumb">
                                            {order.cert_image_url ? (
                                                <ImageWithHoverPreview src={order.cert_image_url} alt={order.cert_name || `Cert ${order.cert_number}`} className="bo-thumb-img" />
                                            ) : <span className="bo-thumb-empty" aria-hidden="true" />}
                                        </span>

                                        <span className="bo-order">
                                            <Link
                                                to={`/orders/${order.order_number}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="bo-chip bo-chip-link bo-mono"
                                                onClick={stop}
                                                title="Open order page"
                                            >
                                                {order.order_number}
                                                <ExternalIcon />
                                            </Link>
                                            {order.date_created && <span className="bo-sub">{formatDate(order.date_created)}</span>}
                                        </span>

                                        <span className="bo-buyer">
                                            {order.buyer_email ? (
                                                <a href={`mailto:${order.buyer_email}`} className="bo-email" onClick={stop} title={`Email ${order.buyer_email}`}>
                                                    {order.buyer_email}
                                                </a>
                                            ) : <span className="bo-sub">No buyer email</span>}
                                        </span>

                                        <span className="bo-price">{formatMoney(order.price)}</span>

                                        <span className={`bo-status bo-status-${s.key}`}>{s.label}</span>

                                        <Chevron open={open} />
                                    </div>

                                    {open && renderDetails(order, s, busy)}
                                </li>
                            );
                        })}
                    </ul>
                )
            )}
        </div>
    );
};

export default OrdersTab;
